import { forwardRef, ReactNode } from "react";
import useConversation from "app/modules/aiAssistant/conversation/hooks/useConversation";
import MessageList from "app/modules/aiAssistant/conversation/messages/MessageList";
import ChatInput, { ChatInputTestIds } from "app/modules/aiAssistant/conversation/input/ChatInput";
import { FeedbackControl } from "app/modules/aiAssistant/conversation/messages/components/feedback/MessageFeedback";

export type ConversationTestIds = ChatInputTestIds & {
  getCopyButton: (messageID: number) => string;
  getFeedback: (control: FeedbackControl, messageID: number) => string;
  getSuggestedPromptButton: (promptID: string) => string;
  thinkingToggleButton: string;
};

type Props = {
  testIds: ConversationTestIds;
  allowAttachments?: boolean;
  className?: string;
  composerClassName?: string;
  // Shown instead of the chat, without losing the draft
  loadingView?: ReactNode;
};

// The open chat's messages and input, shared by the Assistimo page and the pop-up
const ConversationView = forwardRef<HTMLTextAreaElement, Props>(function ConversationView(
  { testIds, allowAttachments = false, className, composerClassName, loadingView },
  inputRef
) {
  const conversation = useConversation();

  if (loadingView) return <>{loadingView}</>;

  const input = (
    <ChatInput
      ref={inputRef}
      testIds={testIds}
      onSend={conversation.sendMessage}
      stopReply={conversation.stopReply}
      draft={conversation.draft}
      setDraft={conversation.setDraft}
      isAwaitingReply={conversation.isAwaitingReply}
      sendDisabled={conversation.isUploading}
      attachments={
        allowAttachments
          ? {
              documents: conversation.documents,
              uploadingDocumentIDs: conversation.uploadingDocumentIDs,
              filesLeftToAttach: conversation.documentsLeftToAttach,
              onFilesAdded: conversation.attachFiles,
              onFileRemoved: conversation.removeDocument,
            }
          : undefined
      }
    />
  );

  const content = (
    <>
      <MessageList
        messages={conversation.messages}
        isAwaitingReply={conversation.isAwaitingReply}
        runSteps={conversation.runSteps}
        getCopyButtonTestId={testIds.getCopyButton}
        getFeedbackTestId={testIds.getFeedback}
        onSuggestedPromptSelect={conversation.sendMessage}
        getSuggestedPromptTestId={testIds.getSuggestedPromptButton}
        thinkingToggleTestId={testIds.thinkingToggleButton}
      />
      {composerClassName ? <div className={composerClassName}>{input}</div> : input}
    </>
  );

  return className ? <div className={className}>{content}</div> : content;
});

export default ConversationView;
